import { Button, useToast, Box, Flex } from '@chakra-ui/react';
import { format } from 'date-fns';
import React, { useState, useEffect, useCallback } from 'react';
import { HiOutlineDocumentReport } from 'react-icons/hi';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import debounce from 'debounce-promise';
import { useAuth } from '../../contexts/auth';
import BoxContent from '../../components/BoxContent';
import FormDatePicker from '../../components/form/FormDatePicker';
import FormGroup from '../../components/form/FormGroup';
import TituloPagina from '../../components/TituloPagina';
import api from '../../services/api';
import AsyncSelect from '../../components/form/AsyncSelect';
import Select from '../../components/form/ReactSelect';

type IUnidade = { uni_codigo: number; uni_sigla: string };

type OptionType = { label: string; value: string };

interface ICategoria {
  id_categoria: number;
  nome: string;
  situacao: string;
}

export const consolidadoSchema = yup.object().shape({
  dateInicio: yup.date().required('Este campo é requerido'),
  dateFim: yup
    .date()
    .required('Este campo é requerido')
    .min(
      yup.ref('dateInicio'),
      'A data fim deve ser maior ou igual a data início',
    ),
  idCategoria: yup.number().notRequired(),
  idSubcategoria: yup.number().notRequired(),
  idUnidade: yup.number().notRequired(),
});

type IForm = {
  dateInicio: Date;
  dateFim: Date;
  idCategoria: number;
  idSubcategoria: number;
  idUnidade: number;
};

export const RelatorioConsolidado: React.FC = () => {
  const toast = useToast();
  const { user } = useAuth();
  const { handleSubmit, control, errors, watch, setValue } = useForm<IForm>({
    resolver: yupResolver(consolidadoSchema),
  });

  const [isLoading, setIsLoading] = useState(false);
  const [optionsCategorias, setOptionsCategorias] = useState<OptionType[]>([]);
  const [optionsSubCategoriaIrso, setOptionsSubCategoriaIrso] = useState<
    OptionType[]
  >([]);
  const [unidadeSelecionada, setUnidadeSelecionada] = useState<OptionType>();

  const loadSubCategorias = useCallback(async (id: number): Promise<void> => {
    const {
      data: { items },
    } = await api.get<{ items: any[] }>('subcategorias', {
      params: { ids_categorias: [id] },
    });

    setOptionsSubCategoriaIrso(
      items
        .filter((item) => item.situacao === '1')
        .map((item) => ({
          label: item.nome,
          value: item.id_subcategoria.toString(),
        })),
    );
  }, []);

  const delayedQuery = debounce(async (query: string) => {
    const { data: unidades } = await api.get<IUnidade[]>('unidades', {
      params: { query, opm_usuario: user.opm?.uni_codigo },
    });

    return unidades?.map<OptionType>((unidade) => ({
      label: unidade.uni_sigla,
      value: unidade.uni_codigo.toString(),
    }));
  }, 500);

  const handleRequestRelatorio = async ({
    dateInicio,
    dateFim,
    idSubcategoria,
    idUnidade,
  }: IForm): Promise<void> => {
    setIsLoading(true);
    try {
      const { data: documento } = await api.get(
        '/escalas/irsos/relatorios/consolidado',
        {
          params: {
            data_inicio: format(dateInicio, 'yyyy-MM-dd'),
            data_fim: format(dateFim, 'yyyy-MM-dd'),
            id_subcategoria: idSubcategoria || undefined,
            id_opm: !idUnidade || idUnidade === -1 ? undefined : idUnidade,
          },
          responseType: 'blob',
        },
      );

      if (documento) {
        const url = window.URL.createObjectURL(
          new Blob([documento], { type: 'text/csv;charset=utf-8;' }),
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `consolidado-${format(dateInicio, 'dd-MM-yyyy')}-${format(
            dateFim,
            'dd-MM-yyyy',
          )}.csv`,
        );

        document.body.appendChild(link);

        link.click();

        link.parentNode?.removeChild(link);
      }
    } catch (error) {
      if (error.response?.data) {
        const response = JSON.parse(await (error.response.data as Blob).text());
        toast({
          title: 'Ocorreu um erro.',
          description: response.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadCategorias = async (): Promise<void> => {
      const {
        data: { items },
      } = await api.get<{ items: ICategoria[] }>('categorias');

      setOptionsCategorias(
        items
          .filter((item) => item.situacao === '1')
          .map(({ nome, id_categoria }) => ({
            label: nome,
            value: id_categoria.toString(),
          })),
      );
    };

    loadCategorias();
  }, []);

  return (
    <>
      <TituloPagina title="Relatório Consolidado" />
      <BoxContent>
        <TituloPagina title="Selecione o período para obter o relatório" />
        <Box as="form" onSubmit={handleSubmit(handleRequestRelatorio)}>
          <Flex flexWrap="wrap">
            <FormGroup name="Data Início" required cols={[2, 3, 12]}>
              <Controller
                control={control}
                name="dateInicio"
                render={({ onChange, value, ...rest }) => (
                  <FormDatePicker
                    selected={value}
                    onChange={(e) => onChange(e as Date)}
                    dateFormat="dd/MM/yyyy"
                    error={errors.dateInicio?.message}
                    {...rest}
                  />
                )}
              />
            </FormGroup>

            <FormGroup name="Data Fim" required cols={[2, 3, 12]}>
              <Controller
                control={control}
                name="dateFim"
                render={({ onChange, value, ...rest }) => (
                  <FormDatePicker
                    selected={value}
                    onChange={(e) => onChange(e as Date)}
                    dateFormat="dd/MM/yyyy"
                    error={errors.dateFim?.message}
                    {...rest}
                  />
                )}
              />
            </FormGroup>

            <FormGroup name="Categoria" cols={[3, 6, 12]}>
              <Controller
                control={control}
                name="idCategoria"
                render={({ onChange, value }) => (
                  <Select
                    options={optionsCategorias}
                    onChange={(option: OptionType) => {
                      onChange(Number.parseInt(option.value, 10));
                      setValue('idSubcategoria', undefined);
                      setOptionsSubCategoriaIrso([]);
                      loadSubCategorias(Number.parseInt(option.value, 10));
                    }}
                    value={
                      optionsCategorias.find(
                        (option) => option.value === value?.toString(),
                      ) ?? null
                    }
                    error={errors.idCategoria?.message}
                  />
                )}
              />
            </FormGroup>

            <FormGroup name="Operação" cols={[3, 6, 12]}>
              <Controller
                control={control}
                name="idSubcategoria"
                render={({ onChange, value }) => (
                  <Select
                    options={optionsSubCategoriaIrso}
                    onChange={(option: OptionType) =>
                      onChange(Number.parseInt(option.value, 10))
                    }
                    value={
                      optionsSubCategoriaIrso.find(
                        (option) => option.value === value?.toString(),
                      ) ?? null
                    }
                    error={errors.idSubcategoria?.message}
                    isDisabled={!watch('idCategoria')}
                  />
                )}
              />
            </FormGroup>

            <FormGroup name="Pesquisar Opm" cols={[2, 6, 12]}>
              <Controller
                control={control}
                name="idUnidade"
                render={({ onChange, name }) => (
                  <AsyncSelect
                    name={name}
                    label="Pesquisar Opm"
                    value={unidadeSelecionada}
                    loadOptions={(value2: string) => delayedQuery(value2)}
                    onChange={(option: OptionType) => {
                      onChange(Number.parseInt(option.value, 10));
                      setUnidadeSelecionada(option);
                    }}
                    error={errors.idUnidade?.message}
                  />
                )}
              />
            </FormGroup>
          </Flex>
          <Button
            leftIcon={<HiOutlineDocumentReport />}
            colorScheme="blue"
            type="submit"
            isLoading={isLoading}
            loadingText="Gerando Relatorio..."
            ml="4"
            mt={7}
          >
            Gerar Relatório
          </Button>
        </Box>
      </BoxContent>
    </>
  );
};
